import React, { useEffect, useRef, useState } from "react";
import { useMobile } from '../hooks/useMobile';
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Avatar,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Visibility, VisibilityOff, Add, OpenInNew } from "@mui/icons-material";
import "./PortalCard.css";
import apiClient from "../utils/apiClient";
import LEDIndicator from "./LEDIndicator";
import { getTimeElapsed } from "../utils/getTimeElapsed";

const OAUTH_PORTAL_CONFIG = {
  1: {
    providerName: "3Shape",
    authUrlPath: "/oauth/communicate/auth-url",
    tokenResultPath: (sessionId) => `/oauth/communicate/token-result/${sessionId}`,
    windowName: "3shape_oauth",
  },
  13: {
    providerName: "AlliedStar",
    authUrlPath: "/oauth/alliedstar/auth-url",
    tokenResultPath: (sessionId) => `/oauth/alliedstar/token-result/${sessionId}`,
    windowName: "alliedstar_oauth",
    // Unlike 3Shape, AlliedStar's callback returns the authenticated user's
    // actual email — so we don't need (or want) it typed in beforehand. The
    // duplicate-portal check moves to after the callback instead (see
    // handleOAuthSuccess), using the confirmed email + region.
    requiresEmailUpfront: false,
    // openid+region credential shape, no access/refresh token cycle — see
    // handleOAuthSuccess's normalization step.
    usesOpenIdCredential: true,
  },
  14: {
    providerName: "NeoConnect",
    authUrlPath: "/oauth/neoconnect/auth-url",
    tokenResultPath: (sessionId) => `/oauth/neoconnect/token-result/${sessionId}`,
    windowName: "neoconnect_oauth",
    // NeoConnect is a whitelabel of AlliedStar's Open Platform — same
    // callback shape, same reasoning as AlliedStar above.
    requiresEmailUpfront: false,
    usesOpenIdCredential: true,
  },
};
const OAUTH_PORTAL_IDS = Object.keys(OAUTH_PORTAL_CONFIG).map(Number);

// Klein label-chipje naast de portalnaam (regio, org-type) — zelfde vorm als
// de admin-chips (Contact/Prio) in WorktypeChips.js, maar gekleurd in het
// merkkleur van de portal (zelfde kleur als de portal-badge/het icoon)
// zodat het chipje duidelijker opvalt dan een neutraal grijs label.
const NameChip = ({ children, color = "#575757", style }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 6px",
      borderRadius: "4px",
      lineHeight: "10px",
      backgroundColor: color,
      color: "#FFF",
      fontFamily: "DM Sans",
      fontSize: "10px",
      fontWeight: 600,
      letterSpacing: "0.88px",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </span>
);
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function darkenColor(hex, amount) {
  const clean = String(hex).replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return hex;
  const num = parseInt(clean, 16);
  const r = Math.max(0, Math.floor(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.floor((num & 0xff) * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const lastFetchVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 0.4, transition: { duration: 0.3, ease: "easeIn" } },
  exit: { opacity: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

const plusButtonVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 4 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  },
};

const PortalCard = ({
  portal,
  position,
  animationKey,
  setPollingBlocked,
  onAddAnother,
  onDialogClose,
  onPortalAdded,
  autoOpenDialog = false,
  isSleeping = false,
}) => {
  const {
    display = "",
    logo = "/images/default-logo.png",
    LastFetch = "N/A",
    hasIssues = false,
    status = "not_added",
    Message = "",
    databaseId,
    id,
    backgroundColor = "#575757",
    name = "",
    organizationId = "",
    organizationName = "",
    region = null,
    StatusCode = "200",
    MinutesSinceLastFetch = 0,
    LastCaseFetched = "N/A",
    unavailable = false,
    organizationType = null,
  } = portal;

  const isOAuthPortal = OAUTH_PORTAL_IDS.includes(id);
  const oauthConfig = OAUTH_PORTAL_CONFIG[id];
  const oauthProviderName = oauthConfig?.providerName || display;
  const requiresEmailUpfront = !isOAuthPortal || oauthConfig?.requiresEmailUpfront !== false;

  const isAuthenticationError = ["400", "401", "403"].includes(String(StatusCode));
  const offlineThreshold = isSleeping ? 10 : 60;
  const isOffline =
    !isAuthenticationError && status === "active" && MinutesSinceLastFetch >= offlineThreshold;
  const isOnline =
    !isAuthenticationError && status === "active" && MinutesSinceLastFetch < offlineThreshold;
  const isNotAdded = status === "not_added";

  const prevLastFetch = useRef(LastFetch);

  // ── FIX 1: isMounted as ref so interval closures always see current value ──
  const isMountedRef = useRef(true);

  // ── FIX 2: oauthStatus as both state (for rendering) and ref (for closures) ──
  const [oauthStatus, setOauthStatus] = useState(null);
  const oauthStatusRef = useRef(null);
  const setOauthStatusSync = (val) => {
    oauthStatusRef.current = val;
    setOauthStatus(val);
  };

  const { isMobile } = useMobile();

  // ── FIX 4: dialogOpen as both state and ref so setTimeout closures are current ──
  const [dialogOpen, setDialogOpen] = useState(autoOpenDialog);
  const dialogOpenRef = useRef(autoOpenDialog);
  const setDialogOpenSync = (val) => {
    dialogOpenRef.current = val;
    setDialogOpen(val);
  };

  const [wasDialogClosed, setWasDialogClosed] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateInput, setDeactivateInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const oauthPollingRef = useRef(null);
  const oauthWindowRef = useRef(null);

  const [attempts, setAttempts] = useState(() => {
    const masterOrganizationId = localStorage.getItem("organization_id");
    const saved = localStorage.getItem(`add_portal_attempts_${id}_${masterOrganizationId}`);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [reauthAttempts, setReauthAttempts] = useState(() => {
    const saved = localStorage.getItem(`reauth_attempts_${databaseId}`);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [lastCase, setLastCase] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [shouldPersistAttempts, setShouldPersistAttempts] = useState(true);
  const [isStatusFinal, setIsStatusFinal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const ledRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const pollingActiveRef = useRef(false);
  const maxAttempts = 3;
  const maxPollingAttempts = 18;
  const isSensitiveFlowActive =
    dialogOpen && (isNotAdded || isAuthenticationError || pollingStatus === "active");

  // ── Mount / unmount ─────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (oauthPollingRef.current) clearInterval(oauthPollingRef.current);
    // AFTER: only force-close the popup if auth did NOT succeed
// On success the popup closes itself via endsession → /oauth/done → window.close()
if (oauthWindowRef.current && !oauthWindowRef.current.closed && oauthStatusRef.current !== "success") {
  oauthWindowRef.current.close();
}
    };
  }, []);

  useEffect(() => {
    if (!wasDialogClosed && !dialogOpenRef.current) setDialogOpenSync(autoOpenDialog);
  }, [autoOpenDialog, wasDialogClosed]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(base64));
        setIsAdmin(payload.role === "Admin" || payload.role === "SuperAdmin");
      } catch (err) {
        console.error("Failed to decode token:", err);
      }
    }
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (
        dialogOpen &&
        isNotAdded &&
        localStorage.getItem("multicompany") === "true"
      ) {
        try {
          const token = localStorage.getItem("token");
          const organizationId = localStorage.getItem("organization_id");
          if (!token || !organizationId)
            throw new Error("Missing token or organization_id");
          const response = await apiClient.get("/organizations", {
            params: { organizationId: parseInt(organizationId, 10) },
            headers: { Authorization: `Bearer ${token}` },
          });
          let orgs = Array.isArray(response.data) ? response.data : [];
          if (orgs.length > 0 && Array.isArray(orgs[0])) orgs = orgs[0];
          setOrganizations(orgs);
        } catch (err) {
          console.error("Failed to fetch organizations:", err);
          setError(
            err.response?.status === 403
              ? "Unauthorized access to organization data."
              : err.response?.data?.message || "Failed to load organizations."
          );
        }
      }
    };
    fetchOrganizations();
  }, [dialogOpen, isNotAdded]);

  useEffect(() => {
    const isBlocking =
      dialogOpen && (isNotAdded || isAuthenticationError || pollingStatus === "active");
    setPollingBlocked?.(isBlocking);
  }, [dialogOpen, isNotAdded, isAuthenticationError, pollingStatus, setPollingBlocked]);

  useEffect(() => {
    if (
      prevLastFetch.current !== LastFetch &&
      prevLastFetch.current !== undefined
    ) {
      ledRef.current?.flash();
    }
    prevLastFetch.current = LastFetch;
  }, [LastFetch]);

  useEffect(() => {
    const fetchLastCase = async () => {
      if (
        dialogOpen &&
        !isNotAdded &&
        !isAuthenticationError &&
        !isOffline &&
        databaseId
      ) {
        try {
          const organizationId = localStorage.getItem("organization_id");
          const token = localStorage.getItem("token");
          if (!organizationId || !token)
            throw new Error("Missing organization_id or token");
          const response = await apiClient.get("/last-case-by-configured-portal", {
            params: { organizationId, configuredPortalId: databaseId },
            headers: { Authorization: `Bearer ${token}` },
          });
          const cases = response.data[0] || {};
          if (cases && Object.keys(cases).length > 0) {
            setLastCase({
              customerName: cases.customer || "Unknown",
              retrievedAt: cases.stamp || "Unknown",
            });
          } else {
            setLastCase(null);
          }
        } catch (err) {
          console.error("Failed to fetch last case:", err);
          setLastCase(null);
        }
      }
    };
    fetchLastCase();
  }, [dialogOpen, isNotAdded, isAuthenticationError, isOffline, databaseId]);

  const positionClasses = [
    position.isFirstInRow ? "first-in-row" : "not-first-in-row",
    position.isLastInRow ? "last-in-row" : "not-last-in-row",
    position.isFirstInColumn ? "first-in-column" : "not-first-in-column",
    position.isLastInColumn ? "last-in-column" : "not-last-in-column",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    let timeoutId = null;
    const flashRandomly = () => {
      if (!ledRef.current) return;
      ledRef.current.flash();
      const randomInterval = Math.random() * (12000 - 7000) + 7000;
      timeoutId = setTimeout(flashRandomly, randomInterval);
    };
    if (!isNotAdded) flashRandomly();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isNotAdded]);

  useEffect(() => {
    if (!shouldPersistAttempts) return;
    const masterOrganizationId = localStorage.getItem("organization_id");
    const attemptsOrgId =
      isNotAdded && localStorage.getItem("multicompany") === "true"
        ? selectedOrgId
        : masterOrganizationId;
    if (attemptsOrgId)
      localStorage.setItem(
        `add_portal_attempts_${id}_${attemptsOrgId}`,
        attempts.toString()
      );
    localStorage.setItem(
      `reauth_attempts_${databaseId}`,
      reauthAttempts.toString()
    );
  }, [
    attempts,
    reauthAttempts,
    id,
    databaseId,
    selectedOrgId,
    shouldPersistAttempts,
  ]);

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      pollingActiveRef.current = false;
    };
  }, []);

  // ── OAuth: stop polling helper ──────────────────────────────
  const stopOAuthPolling = () => {
    if (oauthPollingRef.current) {
      clearInterval(oauthPollingRef.current);
      oauthPollingRef.current = null;
    }
  };

  // ── OAuth: start the interactive login flow ─────────────────
  const handleOAuthLogin = async (configuredPortalId, isReauth, effectiveOrgId) => {
    const masterOrganizationId = localStorage.getItem("organization_id");
    const token = localStorage.getItem("token");

    if (!masterOrganizationId || !token) {
      setError("Missing credentials. Please log in again.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setOauthStatusSync("waiting");

    try {
      const authRes = await apiClient.get(oauthConfig.authUrlPath, {
        params: {
          organizationId: parseInt(masterOrganizationId, 10),
          ...(configuredPortalId !== null && { configuredPortalId }),
          ...(email?.trim() && { email: email.trim() }),
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const { authUrl, sessionId } = authRes.data;

      const width = 540;
      const height = 500;
      const screenCenterX = window.screenX + Math.round(window.outerWidth / 2);
      const screenCenterY = window.screenY + Math.round((window.outerHeight - height) / 2);
      const left = screenCenterX + 210;
      const top = screenCenterY;

      const popup = window.open(
        authUrl,
        oauthConfig.windowName,
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1`
      );
      oauthWindowRef.current = popup;

      if (!popup) {
        setError("Popup was blocked. Please allow popups for this site and try again.");
        setLoading(false);
        setOauthStatusSync(null);
        return;
      }

      let pollCount = 0;
      const maxPollCount = 120;

      oauthPollingRef.current = setInterval(async () => {
        pollCount++;

        // ── FIX 2: use ref instead of stale state ──
        if (popup.closed && oauthStatusRef.current !== "success") {
          stopOAuthPolling();
          if (!isMountedRef.current) return;
          try {
            const result = await apiClient.get(
              oauthConfig.tokenResultPath(sessionId),
              {
                params: { organizationId: parseInt(masterOrganizationId, 10) },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (result.data.status === "success") {
              await handleOAuthSuccess(
                result.data,
                isReauth,
                masterOrganizationId,
                token,
                effectiveOrgId
              );
              return;
            } else if (result.data.status === "email_mismatch") {
              setLoading(false);
              setOauthStatusSync("error");
              setError(result.data.message);
              return;
            }
          } catch (_) {}

          setLoading(false);
          setOauthStatusSync("error");
          setError("Login window was closed before authentication completed.");
          return;
        }

        if (pollCount >= maxPollCount) {
          stopOAuthPolling();
          if (!popup.closed) popup.close();
          if (!isMountedRef.current) return;
          setLoading(false);
          setOauthStatusSync("error");
          setError("Authentication timed out. Please try again.");
          return;
        }

        try {
          const result = await apiClient.get(
            oauthConfig.tokenResultPath(sessionId),
            {
              params: { organizationId: parseInt(masterOrganizationId, 10) },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (result.data.status === "pending") return;

          stopOAuthPolling();
          if (!isMountedRef.current) return;

          if (result.data.status === "success") {
            // ── FIX 3: removed setTimeout popup.close() — backend owns popup
            // lifecycle via endsession → /oauth/done → window.close()
            await handleOAuthSuccess(
              result.data,
              isReauth,
              masterOrganizationId,
              token,
              effectiveOrgId
            );
            setTimeout(() => {
    if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
      oauthWindowRef.current.close();
    }
  }, 8000); // 5s endsession delay + buffer voor redirect
          } else if (result.data.status === "email_mismatch") {
            setLoading(false);
            setOauthStatusSync("error");
            setError(result.data.message);
            // Popup closes itself after endsession redirect chain completes.
            // Keep the 5s fallback in case window.close() in /oauth/done is
            // blocked, but do NOT close immediately — let endsession finish.
            setTimeout(() => {
              if (!popup.closed) popup.close();
            }, 5000);
          } else {
            // Generic error — no endsession redirect, safe to close now.
            if (!popup.closed) popup.close();
            setLoading(false);
            setOauthStatusSync("error");
            setError(result.data.message || "Authentication failed.");
          }
        } catch (err) {
          console.error("OAuth polling error:", err);
        }
      }, 5000);
    } catch (err) {
      console.error("handleOAuthLogin error:", err);
      setLoading(false);
      setOauthStatusSync("error");
      setError(err.response?.data?.message || "Failed to start authentication.");
    }
  };

  // ── OAuth: handle successful token result ───────────────────
  const handleOAuthSuccess = async (
    data,
    isReauth,
    masterOrganizationId,
    token,
    effectiveOrgId
  ) => {
    const { configuredPortalId: returnedPortalId } = data;

    // AlliedStar (and its whitelabels, e.g. NeoConnect) return { openid, region }
    // directly — there is no token exchange, expiry, or refresh cycle like
    // 3Shape's. Normalize it into the same access_token/refresh_token/expiry
    // shape the backend already persists for every OAuth portal, using
    // sentinel values for the fields that don't apply.
    const isOpenIdCredential = oauthConfig?.usesOpenIdCredential === true;
    const access_token = isOpenIdCredential ? data.openid : data.access_token;
    const refresh_token = isOpenIdCredential ? "n/a" : data.refresh_token || "";
    const access_token_expiry = isOpenIdCredential
      ? new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString()
      : data.access_token_expiry;
    const region = isOpenIdCredential ? data.region : undefined;
    // AlliedStar/NeoConnect return the authenticated user's actual email on
    // the callback — prefer that confirmed value over whatever was
    // hand-typed beforehand.
    const confirmedUsername = (isOpenIdCredential && data.email) || email;

    try {
      let finalConfiguredPortalId;

      if (!isReauth) {
        // Portals that don't require the email upfront (AlliedStar) couldn't
        // run the duplicate check before the popup opened — do it now that we
        // know the confirmed email (and region). The AlliedStar login itself
        // has already happened at this point; on a duplicate we simply don't
        // save it rather than trying to undo anything on AlliedStar's side.
        if (!requiresEmailUpfront) {
          const existingPortal = await checkDuplicatePortal(effectiveOrgId, id, confirmedUsername, region);
          if (existingPortal) {
            if (!isMountedRef.current) return;
            setLoading(false);
            setOauthStatusSync("error");
            setError(
              `This portal is already configured with this account${region ? ` (region: ${region})` : ""}.`
            );
            return;
          }
        }

        const response = await apiClient.post(
          "/portal-add-oauth",
          {
            masterOrganizationId: parseInt(masterOrganizationId, 10),
            organizationId: parseInt(effectiveOrgId, 10),
            portalId: id,
            username: confirmedUsername,
            access_token,
            refresh_token,
            access_token_expiry,
            ...(region !== undefined && { region }),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalConfiguredPortalId = response.data.configured_portal_id;
      } else {
        await apiClient.put(
          "/portal-authentication",
          {
            organizationId: parseInt(masterOrganizationId, 10),
            configuredPortalId: returnedPortalId,
            access_token,
            refresh_token,
            access_token_expiry,
            isUpdate: true,
            ...(region !== undefined && { region }),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalConfiguredPortalId = returnedPortalId;
      }

      if (!isMountedRef.current) return;

      setShouldPersistAttempts(false);
      setOauthStatusSync("success");
      setLoading(false);

      if (isReauth) {
        setSuccess("Authentication renewed successfully. Portal is reconnected.");
        setReauthAttempts(0);
        localStorage.removeItem(`reauth_attempts_${databaseId}`);
      } else {
        setSuccess(`Portal connected successfully via ${oauthProviderName} authentication.`);
        setAttempts(0);
        localStorage.removeItem(`add_portal_attempts_${id}_${masterOrganizationId}`);
      }

      if (onPortalAdded) {
        onPortalAdded({
          id,
          databaseId: finalConfiguredPortalId,
          organizationId: effectiveOrgId,
          organizationType:
            isNotAdded && localStorage.getItem("multicompany") !== "true"
              ? localStorage.getItem("org_type") || null
              : organizationType,
          name,
          display,
          logo,
          status: "active",
          backgroundColor,
          Message: "Online",
          StatusCode: "200",
          LastFetch: getTimeElapsed(new Date().toISOString()),
          MinutesSinceLastFetch: 0,
          isConfigured: true,
          hasIssues: false,
        });
      }

      // ── FIX 4: use refs so the closure reads current values ──
      setTimeout(() => {
        if (isMountedRef.current && dialogOpenRef.current) handleCloseDialog(true);
      }, 2000);
    } catch (err) {
      console.error("handleOAuthSuccess: Failed to save tokens:", err);
      if (!isMountedRef.current) return;
      setLoading(false);
      setOauthStatusSync("error");
      setError(
        "Authentication succeeded but failed to save credentials. Please try again."
      );
    }
  };

  const handleCardClick = async () => {
    setDialogOpenSync(true);
    setWasDialogClosed(false);
    setShowPassword(false);
    setEmail("");
    setPassword("");
    setError("");
    setSuccess("");
    setPollingStatus(null);
    setSelectedOrgId("");
    setShouldPersistAttempts(true);
    setIsStatusFinal(false);
    setOauthStatusSync(null);
    if (!isNotAdded) await fetchPortalCredentials();
  };

  const fetchPortalCredentials = async () => {
    setLoading(true);
    setError("");
    try {
      const organizationId = localStorage.getItem("organization_id");
      const token = localStorage.getItem("token");
      if (!organizationId || !token)
        throw new Error("Missing organization_id or token");
      const response = await apiClient.get("/portal-credentials", {
        params: { organizationId, portalId: databaseId },
        headers: { Authorization: `Bearer ${token}` },
      });
      let responseData = response.data;
      if (Array.isArray(responseData) && responseData.length > 0)
        responseData = responseData[0];
      setEmail(responseData?.username || "");
      setPassword(responseData?.password || "");
    } catch (err) {
      console.error("fetchPortalCredentials error:", err);
      setError(err.response?.data?.message || "Failed to load portal credentials");
    } finally {
      setLoading(false);
    }
  };

  // region is null-safe: portals that don't use it (3Shape, credential-based)
  // always pass/compare undefined on both sides, so behavior is unchanged —
  // same portal+email still counts as a duplicate. AlliedStar-style portals
  // pass a real region, so the same email is allowed once per region.
  const checkDuplicatePortal = async (orgId, portalId, email, region) => {
    try {
      const token = localStorage.getItem("token");
      const masterOrganizationId = localStorage.getItem("organization_id");
      if (!token) throw new Error("Missing token");
      const response = await apiClient.get("/configured-portals", {
        params: { organization_id: masterOrganizationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      let portals = Array.isArray(response.data) ? response.data : [];
      if (portals.length > 0 && Array.isArray(portals[0])) portals = portals[0];
      return portals.find(
        (p) =>
          p.portal_id === portalId &&
          (p.organizationId == null || String(p.organizationId) === String(orgId)) &&
          p.username?.toLowerCase() === email?.toLowerCase() &&
          (p.region || null) === (region || null)
      );
    } catch (err) {
      console.error("checkDuplicatePortal error:", err);
      return null;
    }
  };

  const pollPortalStatus = async (configuredPortalId, isReauth) => {
    let pollingAttempts = 0;
    pollingActiveRef.current = true;

    const poll = async () => {
      if (!isMountedRef.current || !pollingActiveRef.current) return;
      pollingAttempts += 1;

      try {
        const organizationId = localStorage.getItem("organization_id");
        const token = localStorage.getItem("token");
        if (!organizationId || !token) throw new Error("Missing credentials");

        const response = await apiClient.get("/configured-portals", {
          params: { organization_id: organizationId },
          headers: { Authorization: `Bearer ${token}` },
        });

        let portals = Array.isArray(response.data) ? response.data : [];
        if (portals.length > 0 && Array.isArray(portals[0])) portals = portals[0];

        const portal = portals.find(
          (p) => p.configured_portal_id === configuredPortalId
        );

        if (!portal) {
          if (pollingAttempts >= maxPollingAttempts)
            throw new Error("Portal not found after max attempts");
          if (pollingActiveRef.current && isMountedRef.current)
            pollingTimeoutRef.current = setTimeout(poll, 5000);
          return;
        }

        const status = String(portal.status);
        if (status !== "-1") setIsStatusFinal(true);

        if (status === "200") {
          pollingActiveRef.current = false;
          if (!isMountedRef.current) return;
          setShouldPersistAttempts(false);
          setError("");
          setPollingStatus("success");

          const newPortalData = {
            id: isNotAdded ? id : databaseId,
            databaseId: isNotAdded ? configuredPortalId : databaseId,
            organizationId:
              isNotAdded && localStorage.getItem("multicompany") === "true"
                ? selectedOrgId
                : organizationId,
            organizationName:
              organizations.find(
                (org) =>
                  org.organizationId ===
                  (isNotAdded && localStorage.getItem("multicompany") === "true"
                    ? selectedOrgId
                    : organizationId)
              )?.organizationName || "",
            organizationType:
              isNotAdded && localStorage.getItem("multicompany") !== "true"
                ? localStorage.getItem("org_type") || null
                : organizationType,
            name,
            display,
            logo,
            status: "active",
            backgroundColor,
            configuredId: isNotAdded ? configuredPortalId : databaseId,
            Message: "Online",
            StatusCode: "200",
            StatusMessage: "OK",
            LastFetch: getTimeElapsed(new Date().toISOString()),
            LastCaseFetched: "N/A",
            active: true,
            MinutesSinceLastFetch: 0,
            isConfigured: true,
            hasIssues: false,
          };
          if (onPortalAdded) onPortalAdded(newPortalData);

          if (isReauth) {
            setSuccess(
              "Password has been updated, and the portal is successfully connected again."
            );
            setReauthAttempts(0);
            localStorage.removeItem(`reauth_attempts_${databaseId}`);
          } else {
            setSuccess("Portal added successfully.");
            setAttempts(0);
            const attemptsOrgId =
              isNotAdded && localStorage.getItem("multicompany") === "true"
                ? selectedOrgId
                : organizationId;
            if (attemptsOrgId)
              localStorage.removeItem(`add_portal_attempts_${id}_${attemptsOrgId}`);
          }

          setLoading(false);
          setTimeout(() => {
            if (isMountedRef.current && dialogOpenRef.current) handleCloseDialog(true);
          }, 2000);
          return;
        } else if (status === "-1") {
          if (pollingAttempts >= maxPollingAttempts)
            throw new Error("Timeout waiting for portal processing");
          if (pollingActiveRef.current && isMountedRef.current)
            pollingTimeoutRef.current = setTimeout(poll, 5000);
          return;
        } else if (["400", "401", "403"].includes(status)) {
          throw new Error(`Authentication failed with status ${status}`);
        } else {
          throw new Error(`Unexpected status: ${status}`);
        }
      } catch (err) {
        console.error("Polling error:", err);
        const isFinalError =
          pollingAttempts >= maxPollingAttempts ||
          err.message.includes("Authentication failed") ||
          err.message.includes("Unexpected status") ||
          err.message.includes("Portal not found after max attempts") ||
          err.message.includes("Timeout waiting for portal processing");

        if (isFinalError) {
          pollingActiveRef.current = false;
          if (!isMountedRef.current) return;

          if (!isReauth) {
            try {
              const organizationId = localStorage.getItem("organization_id");
              const token = localStorage.getItem("token");
              if (organizationId && token) {
                await apiClient.put("/portal-deactivate", undefined, {
                  params: { organizationId, configuredPortalId },
                  headers: { Authorization: `Bearer ${token}` },
                });
              }
            } catch (deactErr) {
              console.error("Deactivation failed:", deactErr);
            }
          }

          if (isReauth) {
            setReauthAttempts((prev) => prev + 1);
            setPollingStatus("failed");
            setError(
              reauthAttempts + 1 < maxAttempts
                ? `Reauthentication failed: ${err.message}. ${
                    maxAttempts - (reauthAttempts + 1)
                  } attempt${
                    maxAttempts - (reauthAttempts + 1) === 1 ? "" : "s"
                  } remaining.`
                : "Maximum reauthentication attempts reached."
            );
          } else {
            setAttempts((prev) => prev + 1);
            setPollingStatus("failed");
            setError(
              attempts + 1 < maxAttempts
                ? `Failed to add portal: ${err.message}. ${
                    maxAttempts - (attempts + 1)
                  } attempt${
                    maxAttempts - (attempts + 1) === 1 ? "" : "s"
                  } remaining.`
                : "Maximum attempts reached. Cannot add portal."
            );
          }

          setLoading(false);
          return;
        }

        if (pollingActiveRef.current && isMountedRef.current)
          pollingTimeoutRef.current = setTimeout(poll, 5000);
      }
    };

    poll();
  };

  // ── Main action handler ─────────────────────────────────────
  const handleReconnect = async () => {
    const masterOrganizationId = localStorage.getItem("organization_id");
    const effectiveOrgId =
      isNotAdded && localStorage.getItem("multicompany") === "true"
        ? selectedOrgId
        : masterOrganizationId;

    if (isNotAdded && attempts >= maxAttempts) {
      setError("Maximum attempts reached. Cannot add portal.");
      return;
    }
    if (isAuthenticationError && reauthAttempts >= maxAttempts) {
      setError("Maximum reauthentication attempts reached.");
      return;
    }
    if (
      isNotAdded &&
      localStorage.getItem("multicompany") === "true" &&
      !selectedOrgId
    ) {
      setError("Please select an organization.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!effectiveOrgId || !token) {
      setError("Missing organization_id or token");
      return;
    }

    if (isOAuthPortal) {
      if (isNotAdded) {
        if (requiresEmailUpfront) {
          if (!email.trim()) {
            setError("Username (email) is required to identify your account.");
            return;
          }
          if (!emailRegex.test(email.trim())) {
            setError("Please enter a valid email address.");
            return;
          }
          setLoading(true);
          setError("");
          setSuccess("");

          try {
            const existingPortal = await checkDuplicatePortal(effectiveOrgId, id, email);
            if (existingPortal) {
              setError("This portal is already configured with the provided username.");
              setLoading(false);
              setTimeout(() => {
                if (isMountedRef.current && dialogOpenRef.current) handleCloseDialog(true);
              }, 5000);
              return;
            }
          } catch (err) {
            setError("Failed to check for duplicate portal.");
            setLoading(false);
            return;
          }
        } else {
          // No email known yet — the duplicate check happens after the OAuth
          // callback instead, once we have the confirmed email (and region).
          setLoading(true);
          setError("");
          setSuccess("");
        }

        await handleOAuthLogin(null, false, effectiveOrgId);
      } else {
        await handleOAuthLogin(databaseId, true, effectiveOrgId);
      }
      return;
    }

    if (!email.trim()) { setError("Username is required"); return; }
    if (!password.trim()) { setError("Password is required"); return; }

    setLoading(true);
    setError("");
    setSuccess("");
    setPollingStatus("active");

    try {
      if (isNotAdded) {
        const existingPortal = await checkDuplicatePortal(effectiveOrgId, id, email);
        if (existingPortal) {
          setError("This portal is already configured with the provided username.");
          setLoading(false);
          setPollingStatus("failed");
          setTimeout(() => {
            if (isMountedRef.current && dialogOpenRef.current) handleCloseDialog(true);
          }, 5000);
          return;
        }

        const response = await apiClient.post("/portal-add", {
          masterOrganizationId,
          organizationId: effectiveOrgId,
          portalId: id,
          username: email,
          password,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        pollPortalStatus(response.data.configured_portal_id, false);
      } else if (isAuthenticationError) {
        await apiClient.put("/portal-credentials", {
          organizationId: masterOrganizationId,
          portalId: databaseId,
          username: email,
          password,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        pollPortalStatus(databaseId, true);
      } else {
        await apiClient.put("/portal-credentials", {
          organizationId: masterOrganizationId,
          portalId: databaseId,
          username: email,
          password,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Credentials updated successfully.");
        setLoading(false);
        setTimeout(() => {
          if (isMountedRef.current && dialogOpenRef.current) handleCloseDialog(true);
        }, 2000);
      }
    } catch (err) {
      console.error("handleReconnect error:", err);
      if (isNotAdded) {
        const configuredPortalId = err.response?.data?.configured_portal_id;
        if (configuredPortalId) {
          pollPortalStatus(configuredPortalId, false);
        } else {
          setError(
            err.response?.status === 404
              ? "Endpoint not found. Please contact support."
              : err.response?.data?.errors
              ? err.response.data.errors.map((e) => e.msg).join("; ")
              : err.response?.data?.message || "Failed to process request."
          );
          setLoading(false);
          setPollingStatus("failed");
        }
      } else {
        setError(
          err.response?.status === 404
            ? "Endpoint not found. Please contact support."
            : err.response?.data?.errors
            ? err.response.data.errors.map((e) => e.msg).join("; ")
            : err.response?.data?.message || "Failed to process request."
        );
        setLoading(false);
        setPollingStatus("failed");
      }
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    handleCloseDialog(true);
    try {
      const organizationId = localStorage.getItem("organization_id");
      const token = localStorage.getItem("token");
      if (!organizationId || !token)
        throw new Error("Missing organization_id or token");
      await apiClient.put("/portal-deactivate", undefined, {
        params: { organizationId, configuredPortalId: databaseId },
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("handleDeactivate error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = (force = false) => {
    if (
      !force &&
      (pollingActiveRef.current ||
        oauthStatusRef.current === "waiting" ||
        (isNotAdded && loading))
    )
      return;

    pollingActiveRef.current = false;
    stopOAuthPolling();
    if (oauthWindowRef.current && !oauthWindowRef.current.closed)
      oauthWindowRef.current.close();

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    setTimeout(() => {
      if (isMountedRef.current) {
        setDialogOpenSync(false);
        setWasDialogClosed(true);
        setPassword("");
        setEmail("");
        setShowPassword(false);
        setError("");
        setSuccess("");
        setLoading(false);
        setPollingStatus(null);
        setSelectedOrgId("");
        setShouldPersistAttempts(true);
        setIsStatusFinal(false);
        setOauthStatusSync(null);
        if (onDialogClose) onDialogClose();
      }
    }, 100);
  };

  const dialogTitle = isNotAdded
    ? "Add Portal"
    : isAuthenticationError
    ? "Reauthenticate"
    : isOffline
    ? "Offline"
    : "Details";

  const oauthWaitingMessage = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        py: 1,
      }}
    >
      <CircularProgress size={32} sx={{ color: backgroundColor }} />
      <Typography
        variant="body2"
        sx={{ color: "#666", textAlign: "center", lineHeight: 1.6 }}
      >
        A {oauthProviderName} login window has opened.
        <br />
        Please complete authentication there.
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#999", textAlign: "center" }}
      >
        This dialog will update automatically once you're done.
      </Typography>
    </Box>
  );

  const dialogMessage = isNotAdded ? (
    <Typography
      variant="body2"
      sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
    >
      <strong>{display}</strong> has not been configured yet.{" "}
      {isOAuthPortal
        ? requiresEmailUpfront
          ? `Enter your email address to start the ${oauthProviderName} authentication flow.`
          : `Click below to start the ${oauthProviderName} authentication flow.`
        : "Please provide credentials to add this portal."}{" "}
      {attempts > 0 &&
        `You have ${maxAttempts - attempts} attempt${
          maxAttempts - attempts === 1 ? "" : "s"
        } remaining.`}
    </Typography>
  ) : isAuthenticationError ? (
    <Typography
      variant="body2"
      sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
    >
      Uh-oh! Seems like something is wrong with <strong>{display}</strong> at{" "}
      <strong>{organizationName}</strong>.{" "}
      {isOAuthPortal
        ? `Click below to re-authenticate with ${oauthProviderName}.`
        : "Try re-entering your credentials to reconnect."}{" "}
      {reauthAttempts > 0 &&
        `You have ${maxAttempts - reauthAttempts} attempt${
          maxAttempts - reauthAttempts === 1 ? "" : "s"
        } remaining.`}
    </Typography>
  ) : isOffline ? (
    <Typography
      variant="body2"
      sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
    >
      <strong>{display}</strong> at <strong>{organizationName}</strong> has
      been offline since{" "}
      {LastFetch !== "N/A" ? LastFetch : "an unknown time"}. Please check the
      portal connection.
    </Typography>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      <Typography
        variant="body2"
        sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
      >
        All is working properly with <strong>{display}</strong> at{" "}
        <strong>{organizationName}</strong>.
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
      >
        Last Online: {LastFetch}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#666", textAlign: "center", lineHeight: 1.5 }}
      >
        Last Fetched:{" "}
        {lastCase
          ? `${lastCase.customerName} (${getTimeElapsed(lastCase.retrievedAt)})`
          : "N/A"}
      </Typography>
    </Box>
  );

  const showInputs = isAuthenticationError || isNotAdded || isOnline;
  const showPasswordField = !isOAuthPortal && showInputs;
  const showEmailField =
    showInputs &&
    !(isOAuthPortal && isAuthenticationError) &&
    !(isOAuthPortal && isNotAdded && !requiresEmailUpfront);

  // Naam-chipjes (regio/CAD) volgen de nachtmodus net als de portal-badge:
  // donker en gedimd zodra de portal slaapt.
  const chipSleeping = isSleeping && isOffline;
  const chipColor = chipSleeping ? "rgba(17, 17, 17, 0.4)" : backgroundColor;
  const chipStyle = {
    position: "relative",
    top: "1px",
    color: chipSleeping ? "rgba(255, 255, 255, 0.4)" : "#FFF",
    transition: "background-color 0.5s ease, color 0.5s ease",
  };

  return (
    <>
      <div
        className={`side-panel-card ${
          hasIssues && !isSleeping ? "has-issues" : ""
        } ${status === "not_added" ? "not-added" : ""} ${positionClasses}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="side-panel-card-inner">
          <div className="portal-card-header">
            <div
              className="portal-badge"
              style={{
                background:
                  (hasIssues && !isSleeping) || status === "not_added"
                    ? "rgba(255, 255, 255, 0.1)"
                    : isSleeping && isOffline
                    ? "rgba(17, 17, 17, 0.4)"
                    : backgroundColor,
                transition: "background 0.5s ease",
              }}
            >
              <img
                src={logo}
                alt={`${display} logo`}
                className="portal-logo"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  opacity: isSleeping && isOffline ? 0.4 : 1,
                  transition: "opacity 0.5s ease",
                }}
              />
            </div>
            <div className="portal-text">
              <div className="portal-name" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span>{display}</span>
                {region && (
                  <NameChip color={chipColor} style={chipStyle}>
                    {region.toUpperCase()}
                  </NameChip>
                )}
                {organizationType === "CAD" && (
                  <NameChip color={chipColor} style={chipStyle}>
                    CAD
                  </NameChip>
                )}
              </div>
              <div className="portal-time-ago">
                {status === "not_added" ? (
                  unavailable ? "Not Available" : "Not Configured"
                ) : (
                  <>
                    Last Online:{" "}
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={`${databaseId || id}-${
                          LastFetch === prevLastFetch.current ? "" : animationKey
                        }`}
                        variants={lastFetchVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        {LastFetch}
                      </motion.span>
                    </AnimatePresence>
                  </>
                )}
              </div>
            </div>
            {Message && (
              <div className="portal-status-chip">
                {isSleeping && isOffline ? "Sleeping" : Message}
                {(!hasIssues || isSleeping) && (
                  <LEDIndicator
                    ref={ledRef}
                    color={isSleeping && isOffline ? "grey" : "green"}
                    size="small"
                  />
                )}
              </div>
            )}
            {localStorage.getItem("multicompany") === "true" &&
              organizationName && (
                <div className="portal-organization-chip">{organizationName}</div>
              )}
          </div>

          {!unavailable && !(isSleeping && (isNotAdded || isAuthenticationError)) && (
            <button
              className="portal-card-button"
              onClick={handleCardClick}
              style={{ cursor: "pointer" }}
            >
              {isAuthenticationError
                ? "Reauthenticate"
                : status === "not_added"
                ? "Add Portal"
                : "View details"}
            </button>
          )}

          {!isNotAdded && !isSleeping && (
            <motion.div
              variants={plusButtonVariants}
              initial="hidden"
              animate={isHovered ? "visible" : "hidden"}
              style={{ position: "absolute", bottom: 8, right: 8, zIndex: 2 }}
            >
              <IconButton
                onClick={() => onAddAnother(portal)}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "white",
                  width: 32,
                  height: 32,
                  boxShadow:
                    "0 0 12px rgba(255,255,255,0.3), 0 0 24px rgba(147,197,253,0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.25)",
                    border: "1px solid rgba(255,255,255,0.9)",
                    boxShadow:
                      "0 0 16px rgba(255,255,255,0.5), 0 0 32px rgba(147,197,253,0.4)",
                  },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </motion.div>
          )}
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => handleCloseDialog(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleCloseDialog(true);
        }}
        TransitionProps={{
          onExited: () => {
            setShowDeactivateConfirm(false);
            setDeactivateInput("");
          },
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            maxWidth: isMobile ? '100%' : 400,
            width: isMobile ? '100%' : 400,
            margin: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <Box
            onClick={() => handleCloseDialog(true)}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1,
              "&:hover": { background: "rgba(0,0,0,0.15)" },
            }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1 1l6 6M7 1L1 7" stroke="#333" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </Box>
          {showDeactivateConfirm ? (
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3, alignItems: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#1a1a1a", textAlign: "center" }}>
                Deactivate Portal
              </Typography>
              <Box sx={{ backgroundColor: "#ffebee", borderRadius: 2, p: 2, width: "100%" }}>
                <Typography variant="body2" sx={{ color: "#c62828", lineHeight: 1.6, textAlign: "center" }}>
                  Deactivating the portal will remove it from the system and prevent the collection of any new cases.
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder="Type deactivate to confirm"
                value={deactivateInput}
                onChange={(e) => setDeactivateInput(e.target.value)}
                autoComplete="off"
                variant="outlined"
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                    "& fieldset": { border: "none" },
                  },
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleDeactivate}
                  disabled={loading || deactivateInput.toLowerCase() !== "deactivate"}
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    py: 1.5,
                    fontSize: "16px",
                    "&:hover": { backgroundColor: "#b71c1c" },
                    "&:disabled": { backgroundColor: "#ccc" },
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Deactivation"}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => { setShowDeactivateConfirm(false); setDeactivateInput(""); }}
                  disabled={loading}
                  sx={{ color: "#666", textTransform: "none", borderRadius: 2, py: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
          <Box
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: "#1a1a1a", mb: 2 }}
            >
              {dialogTitle}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor,
                  color: "white",
                  borderRadius: 3,
                }}
              >
                <img
                  src={logo}
                  alt={`${display} logo`}
                  style={{ width: "66%", height: "66%", objectFit: "contain" }}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#1a1a1a" }}
                  >
                    {display}
                  </Typography>
                  {region && (
                    <NameChip color={backgroundColor} style={{ position: "relative", top: "-1px" }}>
                      {region.toUpperCase()}
                    </NameChip>
                  )}
                  {organizationType === "CAD" && (
                    <NameChip color={backgroundColor} style={{ position: "relative", top: "-1px" }}>
                      CAD
                    </NameChip>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {isAuthenticationError
                    ? "Authentication Error"
                    : isNotAdded
                    ? "Not Configured"
                    : isOnline
                    ? "Online"
                    : isSleeping && isOffline
                    ? `Sleeping since ${LastFetch}`
                    : LastFetch !== "N/A"
                    ? `Offline since ${LastFetch}`
                    : "Offline"}
                </Typography>
              </Box>
            </Box>

            {isNotAdded &&
              localStorage.getItem("multicompany") === "true" &&
              organizations.length > 0 && (
                <Autocomplete
                  fullWidth
                  disablePortal
                  disableClearable
                  options={organizations.filter((org) => org.organizationType !== "Group")}
                  getOptionLabel={(org) =>
                    `${org.organizationName || String(org.organizationId)}${org.organizationType === "CAD" ? " (CAD)" : ""}`
                  }
                  getOptionDisabled={(org) => org.organizationType === "CAD" && id !== 1}
                  value={organizations.find((o) => String(o.organizationId) === String(selectedOrgId)) || null}
                  onChange={(_, newValue) => setSelectedOrgId(newValue?.organizationId || "")}
                  ListboxProps={{
                    style: { maxHeight: 200, overflowY: "auto", padding: "4px 0" },
                  }}
                  componentsProps={{
                    paper: {
                      sx: {
                        borderRadius: 2,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        mt: 0.5,
                        "& .MuiAutocomplete-option": {
                          fontSize: 14,
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          mx: 0.5,
                          '&[aria-selected="true"]': {
                            backgroundColor: "rgba(108,92,231,0.1)",
                            color: "#6c5ce7",
                            fontWeight: 600,
                          },
                          "&:hover": {
                            backgroundColor: "rgba(108,92,231,0.06)",
                          },
                          '&[aria-selected="true"]:hover': {
                            backgroundColor: "rgba(108,92,231,0.15)",
                          },
                        },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select organization"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f5f5",
                          border: "none",
                          borderRadius: 2,
                          fontSize: 14,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputLabel-root": { display: "none" },
                        "& .MuiAutocomplete-endAdornment": { right: 8 },
                      }}
                    />
                  )}
                />
              )}

            {oauthStatus === "waiting" ? (
              oauthWaitingMessage
            ) : loading && (isNotAdded || isAuthenticationError) ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  textAlign: "center",
                  lineHeight: 1.5,
                  py: 2,
                }}
              >
                Checking credentials...
              </Typography>
            ) : success ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#2e7d32",
                  textAlign: "center",
                  lineHeight: 1.5,
                  backgroundColor: "#e8f5e9",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {success}
              </Typography>
            ) : error ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#d32f2f",
                  textAlign: "center",
                  lineHeight: 1.5,
                  backgroundColor: "#ffebee",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {error}
              </Typography>
            ) : (
              dialogMessage
            )}

            {showInputs && oauthStatus !== "waiting" && (
              <Box
                component="form"
                autoComplete="off"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {showEmailField && (
                  <TextField
                    fullWidth
                    value={email}
                    onChange={(e) =>
                      !isOnline &&
                      (isAdmin || !isAuthenticationError) &&
                      setEmail(e.target.value)
                    }
                    variant="outlined"
                    placeholder={
                      isOAuthPortal ? `Email (${oauthProviderName} account)` : "Email or Username"
                    }
                    autoComplete="off"
                    readOnly={isOnline || (isAuthenticationError && !isAdmin)}
                    inputProps={{
                      autoComplete: "off",
                      "data-form-type": "other",
                      "data-lpignore": "true",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f5f5",
                        border: "none",
                        borderRadius: 2,
                        "& fieldset": { border: "none" },
                        "& input": {
                          cursor:
                            isOnline || (isAuthenticationError && !isAdmin)
                              ? "not-allowed"
                              : "text",
                          color:
                            isOnline || (isAuthenticationError && !isAdmin)
                              ? "#666"
                              : "#000",
                        },
                      },
                    }}
                  />
                )}
                {showPasswordField && (
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => !isOnline && setPassword(e.target.value)}
                    variant="outlined"
                    autoComplete="new-password"
                    readOnly={isOnline}
                    InputProps={{
                      autoComplete: "off",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: "#999" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      autoComplete: "new-password",
                      "data-form-type": "other",
                      "data-lpignore": "true",
                      "data-1p-ignore": "true",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f5f5",
                        border: "none",
                        borderRadius: 2,
                        "& fieldset": { border: "none" },
                        "& input": {
                          cursor: isOnline ? "not-allowed" : "text",
                          color: isOnline ? "#666" : "#000",
                        },
                      },
                    }}
                  />
                )}
              </Box>
            )}

            {!success && oauthStatus !== "waiting" && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {(isAuthenticationError || isNotAdded) && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleReconnect}
                    disabled={
                      loading ||
                      (isOAuthPortal && isNotAdded && requiresEmailUpfront && !email.trim()) ||
                      (!isOAuthPortal && (!password.trim() || !email.trim())) ||
                      (isNotAdded && attempts >= maxAttempts) ||
                      (isAuthenticationError && reauthAttempts >= maxAttempts) ||
                      (isNotAdded &&
                        localStorage.getItem("multicompany") === "true" &&
                        !selectedOrgId)
                    }
                    startIcon={isOAuthPortal ? <OpenInNew fontSize="small" /> : null}
                    sx={{
                      backgroundColor: isOAuthPortal ? backgroundColor : "#6c5ce7",
                      color: "white",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      fontSize: "16px",
                      "&:hover": {
                        backgroundColor: isOAuthPortal
                          ? darkenColor(backgroundColor, 0.15)
                          : "#5b4bd6",
                      },
                      "&:disabled": { backgroundColor: "#ccc" },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : isOAuthPortal ? (
                      isNotAdded
                        ? `Connect with ${oauthProviderName}`
                        : `Re-authenticate with ${oauthProviderName}`
                    ) : isNotAdded ? (
                      "Add Portal"
                    ) : (
                      "Reconnect"
                    )}
                  </Button>
                )}

                {!isNotAdded && !isSleeping && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowDeactivateConfirm(true)}
                    disabled={loading}
                    sx={{
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      fontSize: "16px",
                      "&:hover": {
                        backgroundColor: "#ffebee",
                        borderColor: "#d32f2f",
                      },
                      "&:disabled": { color: "#ccc", borderColor: "#ccc" },
                    }}
                  >
                    Deactivate Portal
                  </Button>
                )}
              </Box>
            )}
          </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PortalCard;